import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import styles from './Login.module.css';
import useAuthStore from '../../stores/useAuthStore';
import useDataStore from '../../stores/useDataStore';
import BackgroundPattern from '../../components/Landing/BackgroundPattern';
import arrow from '../../assets/pic/arrow.svg';
import { login, getProfile } from '../../apis/api';
import { clearAccessToken, getAccessToken } from '../../utils/authToken';

import Loading from '../../components/Loading/Loading';
import Snow from '../../components/Landing/Snow';

const Login = () => {
    // ... existing hooks ...
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search || '');
        const force = params.get('force');
        if (force === '1' || force === 'true') return;

        const token = getAccessToken();
        if (!token) return;

        let cancelled = false;
        (async () => {
            try {
                // Validate token: if it works, we are already logged in.
                await getProfile();
                if (cancelled) return;
                navigate('/home', { replace: true });
            } catch {
                // Token exists but is invalid/expired -> clear and stay on login page
                if (cancelled) return;
                clearAccessToken();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [navigate, location.search]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const performLogin = async (username, password) => {
        if (!username || !password) return;
        setIsLoading(true);
        try {
            const response = await login(username, password);

            // 새로운 응답 형식: body에 accessToken, refreshToken
            const accessToken = response.data?.accessToken;
            const refreshToken = response.data?.refreshToken;

            // 기존 응답 형식: header에 Authorization (fallback)
            const headerToken = response.headers['authorization'] || response.headers['Authorization'];

            const token = accessToken || headerToken;

            if (token) {
                // setAccessToken에 refreshToken도 함께 전달
                useAuthStore.getState().login(
                    token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                    username
                );
                localStorage.setItem('accessToken', token.startsWith('Bearer ') ? token : `Bearer ${token}`);

                // Refresh Token 저장
                if (refreshToken) {
                    localStorage.setItem('refreshToken', refreshToken);
                    console.log('Refresh token saved successfully');
                }

                // Optional: only if store supports it (avoid treating login as failed)
                try {
                    const ds = useDataStore.getState?.();
                    if (ds && typeof ds.setActiveTripPlansUser === 'function') {
                        ds.setActiveTripPlansUser(username);
                    }
                } catch {
                    // no-op
                }

                // 최초 로그인(프로필 미설정) 여부 확인
                try {
                    const profile = await getProfile();
                    // 닉네임이나 생년월일이 없으면 최초 설정 페이지로 이동
                    if (!profile.nickname || !profile.birthDate) {
                        navigate('/profile-setup');
                    } else {
                        navigate('/home');
                    }
                } catch (err) {
                    // 프로필 조회 실패시 홈으로 보낼지 에러처리할지.. 보통은 홈으로.
                    console.error("Profile check failed", err);
                    navigate('/home');
                }
            } else {
                alert('로그인에 실패했습니다. (서버 응답 오류)');
            }
        } catch (error) {
            console.error('Login error:', error);
            const message = error.response?.data?.message || '로그인에 실패했습니다.';
            alert(typeof message === 'string' ? message : '아이디와 비밀번호를 확인해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = formData.username.length > 0 && formData.password.length > 0;

    const handleSubmit = () => {
        performLogin(formData.username, formData.password);
    };

    const handleKeyDown = (e) => {
        if (e.isComposing || e.nativeEvent.isComposing) return;
        if (e.key === 'Enter' && isFormValid) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className={styles.container}>
            {isLoading && <Loading />}

            <BackgroundPattern />
            <Snow />

            <h2 className={styles.title}>로그인</h2>

            <div className={styles.formContainer}>
                <div className={styles.inputGroup}>
                    <input
                        type="text"
                        name="username"
                        placeholder="아이디"
                        className={`${styles.inputField} ${formData.username ? styles.filled : ''}`}
                        value={formData.username}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="비밀번호"
                            className={`${styles.inputField} ${formData.password ? styles.filled : ''}`}
                            value={formData.password}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                        <button
                            className={styles.eyeButton}
                            onClick={() => setShowPassword(!showPassword)}
                            type="button"
                        >
                            {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                        </button>
                    </div>
                </div>

                <button
                    className={`${styles.submitButton} ${isFormValid ? styles.active : ''}`}
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                >
                    로그인
                    <img src={arrow} alt="" className={styles.arrowIcon} />
                </button>
            </div>
        </div>
    );
};

export default Login;
